/**
 * Página completa de Notificaciones
 * Vista detallada del sistema de notificaciones para asesores
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Form, Alert, Spinner, ButtonGroup, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/pageheader/pageheader';
// SimpleChat eliminado
import './NotificationsPage.scss';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chatError, setChatError] = useState('');
  const [filter, setFilter] = useState('all');
  const [instanceId, setInstanceId] = useState(1);
  const [asesorId, setAsesorId] = useState(null);

  // Estados para chat simple (pueden eliminarse si no se usará chat)
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // Estados para modal de notificación
  const [showModal, setShowModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    high: 0,
    critical: 0
  });

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, [filter, instanceId, asesorId]);

  const getUserData = () => {
    try {
      const usuario = localStorage.getItem('usuario');
      if (usuario) {
        const userData = JSON.parse(usuario);
        console.log('👤 [NotificationsPage] Usuario autenticado:', userData);

        // Usar exactamente la misma lógica que ChatPanel
        const asesorIdFromStorage = userData.codVendedor; // userData.codVendedor es el código del vendedor
        const instanciaIdFromStorage = userData.instanciaId || userData.instancia_id || 1;

        setInstanceId(instanciaIdFromStorage);
        setAsesorId(asesorIdFromStorage);

        console.log(`🎯 [NotificationsPage] AsesorId configurado: ${asesorIdFromStorage}`);
        console.log(`🤖 [NotificationsPage] Instancia configurada: ${instanciaIdFromStorage}`);
        console.log(`🔗 [NotificationsPage] Instancia del bot: bot${asesorIdFromStorage}`);
      } else {
        console.warn('⚠️ [NotificationsPage] No se encontró usuario autenticado');
        setAsesorId(1); // Fallback por defecto igual que ChatPanel
      }
    } catch (error) {
      console.error('❌ [NotificationsPage] Error obteniendo datos del usuario:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      setChatError('');

      const params = new URLSearchParams();

      switch (filter) {
        case 'unread':
          params.append('solo_no_leidas', 'true');
          break;
        case 'high':
          params.append('prioridad', 'alta');
          break;
        case 'critical':
          params.append('prioridad', 'critica');
          break;
      }

      params.append('limite', '100');

      const authToken = localStorage.getItem('token');
      const response = await fetch(`/api/notifications/${instanceId}?${params}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setNotifications(data.notificaciones || []);
      } else {
        setError(data.message || 'Error al cargar notificaciones');
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setError('Error de conexión al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = new URLSearchParams();

      const response = await fetch(`/api/notifications/${instanceId}/count?${params}`);
      const data = await response.json();

      if (data.success) {
        setStats(prev => ({
          ...prev,
          unread: data.total_no_leidas || 0
        }));
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const markAsRead = async (notificationId, currentAsesorId) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (data.success) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, leida: true, fecha_leida: new Date().toISOString() }
              : notif
          )
        );

        setStats(prev => ({
          ...prev,
          unread: Math.max(0, prev.unread - 1)
        }));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.leida);

      for (const notification of unreadNotifications) {
        await markAsRead(notification.id, notification.asesor_id);
      }

      loadNotifications();
      loadStats();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Función para abrir chat en la bandeja de mensajes (igual que NotificationDropdown)
  const handlePhoneSelect = async (notification) => {
    try {
      console.log('📞 [PHONE SELECT] Navegando a la bandeja de mensajes');
      setChatError('');

      // Marcar como leída si no lo está
      if (!notification.leida) {
        await markAsRead(notification.id, notification.asesor_id);
      }

      if (notification.numero_cliente) {
        // Limpiar el número de cliente pero MANTENER código de país 51
        let numeroLimpio = notification.numero_cliente.toString().trim();

        // Remover sufijos de WhatsApp pero mantener código de país
        numeroLimpio = numeroLimpio.replace(/@[cs]\.whatsapp\.net/g, '');
        numeroLimpio = numeroLimpio.replace(/[^\d]/g, '');

        // Asegurar que tenga el código de país 51 (igual que en NotificationDropdown)
        if (numeroLimpio.length >= 9 && !numeroLimpio.startsWith('51')) {
          numeroLimpio = '51' + numeroLimpio;
        }

        if (!numeroLimpio || numeroLimpio.length < 11) {
          setChatError('Error: Número de cliente inválido.');
          return;
        }

        console.log(`📱 [NotificationsPage] Navegando al chat del cliente: ${numeroLimpio}`);

        // Usar la misma ruta que usa la bandeja de mensajes (igual que NotificationDropdown)
        navigate(`/bandeja/conversaciones?cliente=${numeroLimpio}`);

      } else {
        setChatError('Error: La notificación no tiene número de cliente asociado.');
      }
    } catch (error) {
      console.error('Error selecting phone:', error);
      setChatError(`Error al seleccionar teléfono: ${error.message}`);
    }
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedPhone(null);
  };

  const getNotificationIcon = (tipo) => {
    const icons = {
      'cita_reprogramada': '📅',
      'evaluacion_receta': '📋',
      'sin_receta': '💊',
      'pago_confirmado': '💳',
      'enviar_link_pago': '💻',
      'consulta_sabores': '🍓',
      'consulta_tecnica': '🔍',
      'evaluar_imagen': '🖼️',
      'error_sistema': '🚨'
    };
    return icons[tipo] || '📞';
  };

  const getPriorityBadge = (prioridad) => {
    const badges = {
      'baja': { variant: 'success', text: 'Baja' },
      'normal': { variant: 'info', text: 'Normal' },
      'alta': { variant: 'warning', text: 'Alta' },
      'critica': { variant: 'danger', text: 'Crítica' }
    };
    return badges[prioridad] || badges.normal;
  };

  const getStatusBadge = (estado) => {
    const badges = {
      'pendiente': { variant: 'secondary', text: 'Pendiente', icon: 'fe-clock' },
      'en_proceso': { variant: 'warning', text: 'En Proceso', icon: 'fe-play-circle' },
      'completada': { variant: 'success', text: 'Completada', icon: 'fe-check-circle' },
      'cancelada': { variant: 'danger', text: 'Cancelada', icon: 'fe-x-circle' }
    };
    return badges[estado] || badges.pendiente;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatClientData = (datosCliente) => {
    if (!datosCliente || Object.keys(datosCliente).length === 0) return null;

    const items = [];
    if (datosCliente.nombre) items.push({ label: 'Nombre', value: datosCliente.nombre, icon: 'fe-user' });
    if (datosCliente.distrito) items.push({ label: 'Distrito', value: datosCliente.distrito, icon: 'fe-map-pin' });
    if (datosCliente.direccion) items.push({ label: 'Dirección', value: datosCliente.direccion, icon: 'fe-home' });
    if (datosCliente.cantidad) items.push({ label: 'Cantidad', value: datosCliente.cantidad, icon: 'fe-package' });
    if (datosCliente.metodo_entrega) items.push({ label: 'Entrega', value: datosCliente.metodo_entrega.replace(/_/g, ' '), icon: 'fe-truck' });
    if (datosCliente.producto_solicitado) items.push({ label: 'Producto', value: datosCliente.producto_solicitado, icon: 'fe-shopping-bag' });
    if (datosCliente.ubicacion) items.push({ label: 'Ubicación', value: datosCliente.ubicacion, icon: 'fe-map' });

    return items.length > 0 ? items : null;
  };

  const handlePhoneClick = (e, notification) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('🔥 [PHONE CLICK] Abriendo chat, NO modal');
    handlePhoneSelect(notification);
  };

  const handleNotificationClick = (notification) => {
    console.log('📋 [NOTIFICATION CLICK] Abriendo modal, NO chat');
    console.log('📋 [MODAL STATE] showModal será:', true);
    console.log('📋 [NOTIFICATION DATA]:', notification);
    setSelectedNotification(notification);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    console.log('❌ [MODAL] Cerrando modal');
    setShowModal(false);
    setSelectedNotification(null);
  };

  const filteredNotifications = notifications.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.leida;
      case 'high':
        return notification.prioridad === 'alta';
      case 'critical':
        return notification.prioridad === 'critica';
      default:
        return true;
    }
  });

  return (
    <div>
      <PageHeader
        title="Notificaciones"
        item="Dashboard"
        active_item="Notificaciones"
      />

      <div className="notifications-page">
        <Row className="mb-4">
          <Col lg={3} md={6}>
            <Card className="stat-card stat-total">
              <Card.Body>
                <div className="stat-content">
                  <div className="stat-icon">
                    <i className="fe fe-bell"></i>
                  </div>
                  <div className="stat-details">
                    <h3>{notifications.length}</h3>
                    <p>Total Notificaciones</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="stat-card stat-unread">
              <Card.Body>
                <div className="stat-content">
                  <div className="stat-icon">
                    <i className="fe fe-bell-off"></i>
                  </div>
                  <div className="stat-details">
                    <h3>{stats.unread}</h3>
                    <p>Sin Leer</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="stat-card stat-high">
              <Card.Body>
                <div className="stat-content">
                  <div className="stat-icon">
                    <i className="fe fe-alert-triangle"></i>
                  </div>
                  <div className="stat-details">
                    <h3>{notifications.filter(n => n.prioridad === 'alta').length}</h3>
                    <p>Alta Prioridad</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="stat-card stat-critical">
              <Card.Body>
                <div className="stat-content">
                  <div className="stat-icon">
                    <i className="fe fe-alert-circle"></i>
                  </div>
                  <div className="stat-details">
                    <h3>{notifications.filter(n => n.prioridad === 'critica').length}</h3>
                    <p>Críticas</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <ButtonGroup>
                  <Button
                    variant={filter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter('all')}
                  >
                    Todas
                  </Button>
                  <Button
                    variant={filter === 'unread' ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter('unread')}
                  >
                    Sin Leer ({stats.unread})
                  </Button>
                  <Button
                    variant={filter === 'high' ? 'warning' : 'outline-warning'}
                    onClick={() => setFilter('high')}
                  >
                    Alta Prioridad
                  </Button>
                  <Button
                    variant={filter === 'critical' ? 'danger' : 'outline-danger'}
                    onClick={() => setFilter('critical')}
                  >
                    Críticas
                  </Button>
                </ButtonGroup>
              </Col>

              <Col md={6} className="text-end">
                {stats.unread > 0 && (
                  <Button
                    variant="success"
                    onClick={markAllAsRead}
                    className="me-2"
                  >
                    <i className="fe fe-check-circle me-1"></i>
                    Marcar Todas como Leídas
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    loadNotifications();
                    loadStats();
                  }}
                >
                  <i className="fe fe-refresh-cw me-1"></i>
                  Actualizar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <h5 className="mb-0">
              <i className="fe fe-list me-2"></i>
              Notificaciones {filter !== 'all' && `(${filter})`}
            </h5>
          </Card.Header>

          <Card.Body className="p-0">
            {loading && (
              <div className="text-center p-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 mb-0">Cargando notificaciones...</p>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="m-3">
                <i className="fe fe-alert-circle me-2"></i>
                {error}
              </Alert>
            )}

            {chatError && (
              <Alert variant="warning" className="m-3">
                <i className="fe fe-alert-triangle me-2"></i>
                {chatError}
                <Button
                  variant="link"
                  className="p-0 ms-2"
                  onClick={() => setChatError('')}
                >
                  <i className="fe fe-x"></i>
                </Button>
              </Alert>
            )}

            {!loading && !error && filteredNotifications.length === 0 && (
              <div className="text-center p-5">
                <i className="fe fe-bell-off" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                <h5 className="mt-3 mb-2">No hay notificaciones</h5>
                <p className="text-muted">
                  {filter === 'all'
                    ? 'No tienes notificaciones aún.'
                    : `No hay notificaciones con el filtro "${filter}".`
                  }
                </p>
              </div>
            )}

            {!loading && !error && filteredNotifications.length > 0 && (
              <div className="notification-list-page">
                {filteredNotifications.map((notification, index) => {
                  const clientData = formatClientData(notification.datos_cliente);
                  const statusBadge = getStatusBadge(notification.estado);

                  return (
                    <div
                      key={notification.id}
                      className={`notification-item-page ${!notification.leida ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      style={{ 
                        cursor: 'pointer',
                        border: '1px solid #e9ecef',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#007bff';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.boxShadow = 'none';
                      }}
                      title="Click aquí para ver detalles completos de la notificación"
                    >
                      <div className="notification-content-page">
                        <div className="notification-icon-page">
                          {getNotificationIcon(notification.tipo_notificacion)}
                        </div>

                        <div className="notification-body-page">
                          <div className="notification-header-page">
                            <h6 className="notification-title-page">
                              {notification.titulo}
                              {!notification.leida && (
                                <span className="unread-indicator"></span>
                              )}
                              {/* <small className="text-muted ms-2" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                                (Click para ver detalles)
                              </small> */}
                            </h6>
                            <div className="notification-badges">
                              <Badge
                                bg={getPriorityBadge(notification.prioridad).variant}
                                className="me-2"
                              >
                                {getPriorityBadge(notification.prioridad).text}
                              </Badge>
                              <Badge
                                bg={statusBadge.variant}
                                className="me-2"
                              >
                                <i className={`fe ${statusBadge.icon} me-1`}></i>
                                {statusBadge.text}
                              </Badge>
                              <small className="text-muted">
                                {formatDate(notification.fecha_creacion)}
                              </small>
                            </div>
                          </div>

                          <div className="notification-type-page mb-2">
                            <Badge variant="outline-dark" className="me-2">
                              <i className="fe fe-tag me-1"></i>
                              {notification.tipo_notificacion.replace(/_/g, ' ')}
                            </Badge>
                            {notification.asesor_nombre && (
                              <Badge variant="outline-info">
                                <i className="fe fe-user me-1"></i>
                                {notification.asesor_nombre}
                              </Badge>
                            )}
                          </div>

                          <p className="notification-message-page">
                            {notification.mensaje.split('\n').map((line, index) => (
                              <span key={index}>
                                {line}
                                {index < notification.mensaje.split('\n').length - 1 && <br />}
                              </span>
                            ))}
                          </p>

                          {clientData && (
                            <div className="notification-client-data mb-3">
                              <h6 className="text-muted mb-2">
                                <i className="fe fe-info me-1"></i>
                                Detalles del Cliente:
                              </h6>
                              <div className="client-data-grid">
                                {clientData.map((item, idx) => (
                                  <div key={idx} className="client-data-item">
                                    <i className={`fe ${item.icon} me-1`}></i>
                                    <strong>{item.label}:</strong> {item.value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="notification-meta-page">
                            {notification.numero_cliente && (
                              <button
                                type="button"
                                className="notification-client-page clickable-phone"
                                onClick={(e) => handlePhoneClick(e, notification)}
                                title="Click AQUÍ para abrir chat con este cliente"
                                style={{
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                                }}
                              >
                                <i className="fe fe-phone"></i>
                                {notification.numero_cliente}
                              </button>
                            )}

                            {notification.accion_requerida && (
                              <span className="notification-action-page">
                                <i className="fe fe-arrow-right me-1"></i>
                                {notification.accion_requerida.replace(/_/g, ' ')}
                              </span>
                            )}

                            {notification.leida && notification.fecha_leida && (
                              <span className="notification-read-date">
                                <i className="fe fe-check-circle me-1"></i>
                                Leída: {formatDate(notification.fecha_leida)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="notification-controls-page">
                          {!notification.leida && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id, notification.asesor_id);
                              }}
                              title="Marcar como leída"
                            >
                              <i className="fe fe-check"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Chat simple eliminado */}
        {/* 
        {showChat && selectedPhone && (
          <Row className="mt-4">
            <Col>
              <Card className="chat-container">
                <Card.Body className="p-0">
                  <SimpleChat 
                    phoneNumber={selectedPhone} 
                    onClose={handleCloseChat}
                  />
                </Card.Body>    
              </Card>
            </Col>
          </Row>
        )}
        */}

        {/* Modal de detalles de notificación */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          size="lg" 
          centered
          backdrop="static"
          style={{ zIndex: 9999 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fe fe-bell me-2"></i>
              Detalles de la Notificación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedNotification && (
              <div className="notification-modal-details">
                <Row className="mb-3">
                  <Col>
                    <div className="d-flex align-items-center mb-3">
                      <div className="notification-icon-large me-3">
                        {getNotificationIcon(selectedNotification.tipo_notificacion)}
                      </div>
                      <div>
                        <h5 className="mb-1">{selectedNotification.titulo}</h5>
                        <div className="d-flex flex-wrap gap-2">
                          <Badge
                            bg={getPriorityBadge(selectedNotification.prioridad).variant}
                          >
                            {getPriorityBadge(selectedNotification.prioridad).text}
                          </Badge>
                          <Badge
                            bg={getStatusBadge(selectedNotification.estado).variant}
                          >
                            <i className={`fe ${getStatusBadge(selectedNotification.estado).icon} me-1`}></i>
                            {getStatusBadge(selectedNotification.estado).text}
                          </Badge>
                          <Badge variant="outline-dark">
                            <i className="fe fe-tag me-1"></i>
                            {selectedNotification.tipo_notificacion.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <strong><i className="fe fe-calendar me-1"></i> Fecha de Creación:</strong>
                    <br />
                    {formatDate(selectedNotification.fecha_creacion)}
                  </Col>
                  <Col md={6}>
                    {selectedNotification.asesor_nombre && (
                      <>
                        <strong><i className="fe fe-user me-1"></i> Asesor:</strong>
                        <br />
                        {selectedNotification.asesor_nombre}
                      </>
                    )}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <strong><i className="fe fe-message-square me-1"></i> Mensaje:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {selectedNotification.mensaje.split('\n').map((line, index) => (
                        <div key={index}>
                          {line}
                          {index < selectedNotification.mensaje.split('\n').length - 1 && <br />}
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>

                {selectedNotification.numero_cliente && (
                  <Row className="mb-3">
                    <Col>
                      <strong><i className="fe fe-phone me-1"></i> Cliente:</strong>
                      <div className="mt-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseModal();
                            handlePhoneSelect(selectedNotification);
                          }}
                        >
                          <i className="fe fe-message-circle me-1"></i>
                          Abrir Chat: {selectedNotification.numero_cliente}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                )}

                {formatClientData(selectedNotification.datos_cliente) && (
                  <Row className="mb-3">
                    <Col>
                      <strong><i className="fe fe-info me-1"></i> Información del Cliente:</strong>
                      <div className="mt-2">
                        <Card className="border-0 bg-light">
                          <Card.Body className="p-3">
                            <Row>
                              {formatClientData(selectedNotification.datos_cliente).map((item, idx) => (
                                <Col md={6} key={idx} className="mb-2">
                                  <div className="d-flex align-items-center">
                                    <i className={`fe ${item.icon} me-2 text-primary`}></i>
                                    <div>
                                      <small className="text-muted">{item.label}:</small>
                                      <div className="fw-bold">{item.value}</div>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        </Card>
                      </div>
                    </Col>
                  </Row>
                )}

                {selectedNotification.accion_requerida && (
                  <Row className="mb-3">
                    <Col>
                      <strong><i className="fe fe-arrow-right me-1"></i> Acción Requerida:</strong>
                      <div className="mt-2">
                        <Badge variant="warning" className="p-2">
                          {selectedNotification.accion_requerida.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                )}

                {selectedNotification.leida && selectedNotification.fecha_leida && (
                  <Row>
                    <Col>
                      <div className="text-success">
                        <i className="fe fe-check-circle me-1"></i>
                        <strong>Leída:</strong> {formatDate(selectedNotification.fecha_leida)}
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {selectedNotification && !selectedNotification.leida && (
              <Button
                variant="primary"
                onClick={() => {
                  markAsRead(selectedNotification.id, selectedNotification.asesor_id);
                  handleCloseModal();
                }}
              >
                <i className="fe fe-check me-1"></i>
                Marcar como Leída
              </Button>
            )}
            <Button variant="secondary" onClick={handleCloseModal}>
              <i className="fe fe-x me-1"></i>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default NotificationsPage;